import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  FormHelperText,
  InputAdornment,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import FormPreview from "./Components/FormPreview";
import ButtonOne from "../../common/normal-button";
import StepTabs from "../../common/step-tabs";
import { useAuth } from "../../hooks/AuthProvider";
import {
  FORMS_CATEGORIES_PRESETS,
  FORMS_CHECK_NAME,
  FORMS_FORM_DRAFT,
  FORMS_FORMS_CREATE,
} from "../../config/urls";
import ButtonTwo from "../../common/dashboard-button";
import { formatTitle } from "../utils";
import { Save } from "@mui/icons-material";
import {
  ColorsConstants,
  borderColors,
  grayFontColor,
} from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import MultiSelectDropdown from "../../common/multi-select-dropdown";
import {
  clearUnsavedChanges,
  useUnsavedChanges,
} from "../../common/unsaved-changes";
import { getFormNameError } from "./Components/utils";

const NAME_MAX_LENGTH = 200;

const CreateForm = ({ onNavigate, pageParams }) => {
  const tenant = localStorage.getItem("tenant");
  const { getValidToken } = useAuth();
  const trackChanges = useUnsavedChanges();

  // Set once the draft has been saved (or when coming back from the builder).
  const [formId, setFormId] = useState(pageParams?.id || null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [presets, setPresets] = useState([]);
  const [persistedFormJson, setPersistedFormJson] = useState(
    pageParams?.form_json || null,
  );
  const [name, setName] = useState(pageParams?.name || "");
  const [categories, setCategories] = useState(pageParams?.categories || []);
  const [presetId, setPresetId] = useState(pageParams?.presetId || "");
  const [suggestedFromStarter, setSuggestedFromStarter] = useState(false);
  const [selectedScreenId, setSelectedScreenId] = useState("");
  // idle | checking | available | taken
  const [nameStatus, setNameStatus] = useState("idle");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({
    message: "",
    severity: "error",
  });

  const labelSx = {
    ...typography.bodySmall,
    fontWeight: 500,
    color: ColorsConstants.primaryBackgroundColor,
    mb: 0.75,
  };

  const selectedPreset = presets?.find((preset) => preset.id === presetId);
  const previewScreens = selectedPreset?.form_json?.screens || [];

  useEffect(() => {
    fetchCategoriesAndPresets();
  }, []);

  // Warn before leaving with unsaved changes. The baseline is taken once the
  // starter list has loaded so the default starter doesn't count as an edit.
  useEffect(() => {
    if (loading || presets.length === 0) return;
    trackChanges({ name, categories, presetId });
  }, [name, categories, presetId, loading, presets, trackChanges]);

  // Live (debounced) uniqueness check for the form name.
  useEffect(() => {
    if (!name.trim()) {
      setNameStatus("idle");
      return undefined;
    }

    setNameStatus("checking");
    let cancelled = false;
    const timer = setTimeout(async () => {
      const available = await checkNameAvailable(name.trim());
      if (!cancelled) setNameStatus(available ? "available" : "taken");
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [name, formId]);

  // Resolves true when the name is free. A failed check is not treated as a
  // conflict; the backend still enforces uniqueness on save.
  const checkNameAvailable = async (value) => {
    try {
      const token = await getValidToken();
      const excludeParam = formId ? `&exclude_id=${formId}` : "";
      const response = await fetch(
        `${FORMS_CHECK_NAME}?name=${encodeURIComponent(value)}${excludeParam}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
          },
        },
      );
      if (!response.ok) return true;
      const data = await response.json();
      return data.available !== false;
    } catch (error) {
      return true;
    }
  };

  // Picking a starter seeds the category suggestion.
  const handleStarterChange = (preset) => {
    setPresetId(preset.id);
    setPersistedFormJson(null);
    setSelectedScreenId("");
    if (preset.suggested_categories?.length > 0) {
      setCategories(preset.suggested_categories);
      setSuggestedFromStarter(true);
    }
  };

  const handleCategoriesChange = (values) => {
    setCategories(values);
    setSuggestedFromStarter(false);
  };

  const fetchCategoriesAndPresets = async () => {
    const token = await getValidToken();

    setLoading(true);
    setAlert({ message: "", severity: "error" });

    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };

      const response = await fetch(FORMS_CATEGORIES_PRESETS, { headers });

      if (!response.ok)
        throw new Error("Failed to fetch categories and presets");

      const data = await response.json();

      setCategoryOptions(data.form_category_options || []);
      setPresets(data.form_presets);
      if (!pageParams?.presetId && data.form_presets?.length > 0) {
        handleStarterChange(data.form_presets[0]);
      }
    } catch (error) {
      console.error("Error fetching categories and presets:", error);
      setAlert({
        message: "Failed to fetch categories and presets",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Saves the draft and returns its id, or null when it couldn't be saved.
  const persistDraft = async ({ requireCategory }) => {
    if (!(await validate({ requireCategory }))) return null;

    const token = await getValidToken();

    setSaving(true);
    setAlert({ message: "", severity: "error" });

    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Tenant-Id": tenant,
      };
      const resolvedFormJson = persistedFormJson ?? selectedPreset?.form_json;
      const body = {
        name,
        status: "DRAFT",
        categories,
        form_json: resolvedFormJson,
      };

      // First save creates the draft; later saves update it in place.
      const response = await fetch(
        formId ? FORMS_FORM_DRAFT(formId) : FORMS_FORMS_CREATE,
        {
          headers,
          method: formId ? "PUT" : "POST",
          body: JSON.stringify(body),
        },
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || "Failed to save draft");
      }

      if (!formId && data?.id) setFormId(data.id);
      clearUnsavedChanges();

      setAlert({
        message: "Draft saved successfully",
        severity: "success",
      });
      return formId || data?.id || null;
    } catch (error) {
      console.error("Error saving draft:", error);
      setAlert({
        message: error?.message || "Failed to save draft",
        severity: "error",
      });
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    // A draft only needs a valid name.
    await persistDraft({ requireCategory: false });
  };

  // Next saves the draft first: the builder works on a saved form.
  const handleSubmit = async () => {
    const id = await persistDraft({ requireCategory: true });
    if (!id) return;

    clearUnsavedChanges();
    onNavigate("form_builder", {
      id,
      name,
      categories,
      presetId: selectedPreset?.id,
      source: "create_form",
    });
  };

  const validate = async ({ requireCategory }) => {
    const nameError = getFormNameError(name);
    if (nameError) {
      setAlert({ message: nameError, severity: "error" });
      return false;
    }
    if (requireCategory && categories.length === 0) {
      setAlert({
        message: "Select at least one category",
        severity: "error",
      });
      return false;
    }

    if (!presetId || !selectedPreset) {
      setAlert({ message: "Please select a starter", severity: "error" });
      return false;
    }

    if (!(await checkNameAvailable(name.trim()))) {
      setNameStatus("taken");
      setAlert({
        message: "A form with this name already exists",
        severity: "error",
      });
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (pageParams?.form_json !== undefined) {
      setPersistedFormJson(pageParams.form_json);
    }
  }, [pageParams?.form_json]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack sx={{ px: 2 }} spacing={2}>
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}
      >
        <Stack>
          <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
            Message Templates › WhatsApp Forms › Create Form
          </Typography>
          <Typography sx={typography.h5}>Create WhatsApp Form</Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <ButtonTwo
            title="Save as Draft"
            onClick={handleSaveDraft}
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
            disabled={saving}
          />
          <ButtonOne title="Next" onClick={handleSubmit} disabled={saving} />
        </Stack>
      </Stack>

      <StepTabs
        steps={[
          { label: "Details" },
          { label: "Build screens" },
          { label: "Data & destinations", disabled: true },
        ]}
        current={0}
        onStepClick={(index) => index === 1 && !saving && handleSubmit()}
      />

      {alert && alert.message && (
        <Alert
          severity={alert.severity}
          sx={{ "& .MuiAlert-message": typography.bodyLarge }}
        >
          {alert.message}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          width: "100%",
          border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 360px" },
          }}
        >
          <Stack
            spacing={2.5}
            sx={{
              p: 2.25,
              minWidth: 0,
              borderRight: {
                lg: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
              },
              borderBottom: {
                xs: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
                lg: "none",
              },
            }}
          >
            {/* Name */}
            <Box>
              <Typography
                sx={labelSx}
              >
                Name
              </Typography>

              <TextField
                placeholder="Enter Name"
                variant="outlined"
                value={name}
                onChange={(e) => {
                  if (e.target.value.length <= NAME_MAX_LENGTH) {
                    setName(e.target.value);
                  }
                }}
                fullWidth
                error={nameStatus === "taken"}
                helperText={
                  nameStatus === "taken"
                    ? "A form with this name already exists"
                    : ""
                }
                inputProps={{ maxLength: NAME_MAX_LENGTH }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        sx={{
                          ...typography.bodySmall,
                          color: ColorsConstants.primaryGray60,
                        }}
                      >
                        {name.length}/{NAME_MAX_LENGTH}
                      </Typography>
                    </InputAdornment>
                  ),
                  sx: typography.bodyLarge,
                }}
                InputLabelProps={{
                  sx: typography.bodyLarge,
                }}
              />
            </Box>

            {/* Category */}
            <Box>
              <Typography sx={labelSx}>
                Category
                <Box component="span" sx={{ ml: 0.75, fontWeight: 400, color: grayFontColor }}>
                  Required by Meta · select one or more
                </Box>
              </Typography>

              <MultiSelectDropdown
                options={categoryOptions}
                value={categories}
                onChange={handleCategoriesChange}
                placeholder="Select Category"
                searchPlaceholder="Search categories"
              />
              {suggestedFromStarter && (
                <FormHelperText sx={typography.bodySmall}>
                  Suggested from the starter
                </FormHelperText>
              )}
            </Box>

            {/* Start from */}
            {presets && presets.length > 0 && (
              <FormControl>
                <Typography
                  sx={labelSx}
                >
                  Start from
                </Typography>

                <RadioGroup
                  name="start-from-radio-group"
                  value={String(presetId)}
                  onChange={(e) => {
                    const preset = presets.find(
                      (p) => String(p.id) === e.target.value,
                    );
                    if (!preset || preset.id === presetId) return;
                    // persistedFormJson is set once screens were edited in the builder.
                    if (
                      persistedFormJson &&
                      !window.confirm(
                        "Replace your screens with this starter? Your changes will be lost.",
                      )
                    ) {
                      return;
                    }
                    handleStarterChange(preset);
                  }}
                >
                  {presets.map((preset) => {
                    const isSelected = presetId === preset.id;

                    return (
                      <Box
                        key={preset.id}
                        sx={{
                          border: isSelected
                            ? `1px solid ${ColorsConstants.primaryGray100}`
                            : "1px solid transparent",
                          borderRadius: "6px",
                          px: 1.5,
                          py: 0.25,
                          transition: "border-color 0.2s ease",
                        }}
                      >
                        <FormControlLabel
                          value={String(preset.id)}
                          sx={{ width: "100%", m: 0 }}
                          control={
                            <Radio
                              size="small"
                              sx={{
                                color: ColorsConstants.primaryGray100,
                                "&.Mui-checked": {
                                  color: ColorsConstants.primaryGray100,
                                },
                              }}
                            />
                          }
                          label={
                            <Typography
                              sx={{
                                ...typography.bodySmall,
                                color: ColorsConstants.primaryBackgroundColor,
                              }}
                            >
                              {formatTitle(preset.name)}
                            </Typography>
                          }
                        />
                      </Box>
                    );
                  })}
                </RadioGroup>
              </FormControl>
            )}
          </Stack>
          <Stack spacing={1.5} sx={{ p: 2.25, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>
                Preview
              </Typography>
              {previewScreens.length > 0 && (
                <Select
                  size="small"
                  value={selectedScreenId || previewScreens[0].id}
                  onChange={(e) => setSelectedScreenId(e.target.value)}
                  sx={{ ...typography.bodySmall, flex: 1, minWidth: 0 }}
                >
                  {previewScreens.map((screen, index) => (
                    <MenuItem
                      key={screen.id}
                      value={screen.id}
                      sx={typography.bodySmall}
                    >
                      {`Screen ${index + 1}: ${screen.title || screen.id}`}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </Stack>
            <FormPreview
              formJson={selectedPreset?.form_json}
              selectedScreenId={selectedScreenId || undefined}
              presetId={presetId}
              compact
              onScreenChange={setSelectedScreenId}
            />
          </Stack>
        </Box>
      </Paper>
    </Stack>
  );
};

export default CreateForm;
